import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { Expedition, Language } from '../types';
import { TRANSLATIONS } from '../translations/translations';
import { translateExpeditions as _translateExpeditions } from '../translations/expeditionTranslations';

interface TranslationContextProps {
	currentLanguage: Language;
	setLanguage: (lang: Language) => void;
	t: (key: keyof (typeof TRANSLATIONS)['en']) => string;
	translateExpeditions: (expeditions: Expedition[]) => Expedition[];
	detectingLocation: boolean;
	detectedCountry: string;
}

const TranslationContext = createContext<TranslationContextProps | undefined>(undefined);

export const TranslationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [currentLanguage, setCurrentLanguageState] = useState<Language>('en');
	const [detectingLocation, setDetectingLocation] = useState<boolean>(false);
	const [detectedCountry] = useState<string>('');

	const setLanguage = (lang: Language) => {
		setCurrentLanguageState(lang);
		localStorage.setItem('user-language', lang);
	};

	useEffect(() => {
		// 1. Check if user explicitly chose a language in a previous session
		const savedLang = localStorage.getItem('user-language') as Language | null;
		if (savedLang && ['en', 'it', 'fr', 'de'].includes(savedLang)) {
			setCurrentLanguageState(savedLang);
		} else {
			// 2. Default to English
			setCurrentLanguageState('en');
		}
		setDetectingLocation(false);
	}, []);

	const t = (key: keyof (typeof TRANSLATIONS)['en']): string => {
		const dictionary = TRANSLATIONS[currentLanguage] || TRANSLATIONS['en'];
		return (
			(dictionary as Record<string, string>)[key] || (TRANSLATIONS['en'] as Record<string, string>)[key] || String(key)
		);
	};

	const translateExpeditions = useCallback(
		(expeditions: Expedition[]) => _translateExpeditions(expeditions, currentLanguage),
		[currentLanguage],
	);

	return (
		<TranslationContext.Provider
			value={{
				currentLanguage,
				setLanguage,
				t,
				translateExpeditions,
				detectingLocation,
				detectedCountry,
			}}
		>
			{children}
		</TranslationContext.Provider>
	);
};

export const useTranslation = () => {
	const context = useContext(TranslationContext);
	if (context === undefined) {
		throw new Error('useTranslation must be used within a TranslationProvider');
	}
	return context;
};
